import { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Epic 112.14 — the floating assistant is now the in-house
// AssistantWidget (talks to the hardened /api/v1/ai backend), replacing
// the @numentech/ai-chatbot sibling-repo stub that never resolved in
// Docker. Self-hides via /ai/assistant/status when no provider is
// configured for the tenant (112.16).
import { AssistantWidget } from '@/components/AssistantWidget';
import {
  LayoutDashboard,
  Building2,
  UserPlus,
  Megaphone,
  TrendingUp,
  ClipboardList,
  Layers,
  Search,
  Bot,
  Briefcase,
  Package,
  BarChart3,
  Settings,
  Users,
  PanelLeftClose,
  PanelLeft,
  PanelLeftOpen,
  ChevronDown,
  Menu,
  X,
  Heart,
  Bell,
  AlertTriangle,
  Cpu,
  CheckSquare,
  AtSign,
  ShieldAlert,
  ShieldCheck,
  Workflow,
  Map,
  Inbox,
  Mail,
  PhoneOff,
  Phone,
  Coins,
  Filter as Funnel,
  Target,
  Compass,
  Crown,
  Database,
  Code2,
  Zap,
  ArrowRightLeft,
  BookOpen,
  DollarSign,
  CalendarCheck,
  LayoutTemplate,
  ListOrdered,
  ServerCog,
  MessageSquare,
  Radio,
  Monitor,
  FileSignature,
  FileText,
  Handshake,
  Newspaper,
  MessagesSquare,
  FolderKanban,
  CalendarDays,
  HardDrive,
  Clock,
  Smartphone,
  Globe,
  CreditCard,
  Warehouse as WarehouseIcon,
  AppWindow,
  Mails,
  Puzzle,
  Plug,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, refreshSession } from '@/lib/apiClient';
import { publishSession, clearSession } from '@/lib/sessionBridge';
import { REMOTE_REGISTRY } from '@/lib/remoteRegistry';
import { useAuthStore } from '@/stores/authStore';
import { useCapabilities } from '@/hooks/useCapabilities';
import { useIdleLogout } from '@/hooks/useIdleLogout';
import { UpdateBanner } from '@/components/UpdateBanner';
import { MaintenanceBanner } from '@/components/MaintenanceBanner';
import { UserMenu } from '@/components/UserMenu';
import { CommandPalette } from '@/components/CommandPalette';
import { BuildInfo } from '@/components/BuildInfo';
import { useBranding } from '@/hooks/useBranding';
import { cn } from '@/lib/cn';
import { CreditBalanceWidget } from '@/components/CreditBalanceWidget';

type SidebarMode = 'icons' | 'labels' | 'expanded';

const MODE_KEY = 'kairos.sidebar-mode';
const COLLAPSED_SECTIONS_KEY = 'kairos.sidebar-collapsed-sections';
const MODES: SidebarMode[] = ['icons', 'labels', 'expanded'];
const MODE_ICONS: Record<SidebarMode, React.ReactNode> = {
  icons: <PanelLeftClose className="h-3.5 w-3.5" />,
  labels: <PanelLeft className="h-3.5 w-3.5" />,
  expanded: <PanelLeftOpen className="h-3.5 w-3.5" />,
};
const MODE_WIDTHS: Record<SidebarMode, string> = {
  icons: 'w-16',
  labels: 'w-20',
  expanded: 'w-64',
};

interface NavItem {
  to: string;
  labelKey: string;
  testId: string;
  icon: React.ReactNode;
  /** Optional plaintext fallback shown if the i18n key is missing. */
  fallbackLabel?: string;
  /**
   * Story 121.7 — optional capability key (module catalogue, see
   * `docs/engineering/module-catalogue.md`). When set, the item is
   * filtered out by the shell if the tenant does not have the
   * module entitled+activated. Omit to keep the item always visible
   * (used today for items that span multiple modules or belong to
   * foundation modules like `core.records`).
   */
  capabilityKey?: string;
}

interface NavSection {
  /** Optional clickable section header (i18n key). Hidden when isCompact = true. */
  headerKey?: string;
  /** When true, the section starts collapsed; user can toggle via the chevron. */
  defaultCollapsed?: boolean;
  items: NavItem[];
}

/**
 * Story 46.8 — campaign-centric sidebar for elevated roles.
 *
 * Sales reps and pre-sales (and the rare unauth case) see the legacy
 * flat nav with Inbox up top. Tenant admin / sales manager / platform
 * admin see Portfolio first, then Action items (Inbox / Tasks / Dashboard),
 * then a "Records" group, then Admin tools.
 */
const REP_NAV: NavSection[] = [
  {
    items: [
      { to: '/inbox', labelKey: 'nav.inbox', testId: 'nav-inbox', icon: <Inbox className="h-5 w-5" />, fallbackLabel: 'Inbox', capabilityKey: 'inbox' },
      { to: '/cockpit/sdr', labelKey: 'workspace.nav_sdr', testId: 'nav-sdr-cockpit', icon: <LayoutTemplate className="h-5 w-5" /> },
      { to: '/handoff-inbox', labelKey: 'handoff.inbox_title', testId: 'nav-handoff-inbox', icon: <ArrowRightLeft className="h-5 w-5" /> },
      { to: '/conversations', labelKey: 'nav.conversations', testId: 'nav-conversations', icon: <MessageSquare className="h-5 w-5" />, capabilityKey: 'inbox' },
      { to: '/dashboard', labelKey: 'nav.dashboard', testId: 'nav-dashboard', icon: <LayoutDashboard className="h-5 w-5" />, fallbackLabel: 'Dashboard' },
      { to: '/feed', labelKey: 'livefeed.nav.title', testId: 'nav-feed', icon: <Newspaper className="h-5 w-5" />, fallbackLabel: 'Company Feed' },
      { to: '/messages', labelKey: 'chat.nav.title', testId: 'nav-messages', icon: <MessagesSquare className="h-5 w-5" />, fallbackLabel: 'Messages' },
      { to: '/workgroups', labelKey: 'workgroups.nav.title', testId: 'nav-workgroups', icon: <FolderKanban className="h-5 w-5" />, fallbackLabel: 'Workgroups' },
      { to: '/time-clock', labelKey: 'timeman.nav.title', testId: 'nav-time-clock', icon: <Clock className="h-5 w-5" />, fallbackLabel: 'Time Clock' },
      { to: '/calendar', labelKey: 'calendar.nav.title', testId: 'nav-calendar', icon: <CalendarDays className="h-5 w-5" />, fallbackLabel: 'Calendar' },
      { to: '/drive', labelKey: 'drive.nav.title', testId: 'nav-drive', icon: <HardDrive className="h-5 w-5" />, fallbackLabel: 'Drive' },
      { to: '/account/devices', labelKey: 'devices.nav.title', testId: 'nav-devices', icon: <Smartphone className="h-5 w-5" />, fallbackLabel: 'My Devices' },
      { to: '/account/email-incoming', labelKey: 'incomingEmail.nav.title', testId: 'nav-incoming-email', icon: <Mails className="h-5 w-5" />, fallbackLabel: 'Incoming Email' },
      { to: '/surveys', labelKey: 'surveys.nav.title', testId: 'nav-surveys', icon: <ClipboardList className="h-5 w-5" />, fallbackLabel: 'Surveys' },
      { to: '/esign', labelKey: 'esign.nav.title', testId: 'nav-esign', icon: <FileSignature className="h-5 w-5" />, fallbackLabel: 'E-Sign' },
      { to: '/accounts', labelKey: 'nav.accounts', testId: 'nav-accounts', icon: <Building2 className="h-5 w-5" /> },
      { to: '/leads', labelKey: 'nav.leads', testId: 'nav-leads', icon: <UserPlus className="h-5 w-5" />, capabilityKey: 'leads' },
      { to: '/campaigns', labelKey: 'nav.campaigns', testId: 'nav-campaigns', icon: <Megaphone className="h-5 w-5" />, capabilityKey: 'campaigns' },
      { to: '/opportunities', labelKey: 'nav.opportunities', testId: 'nav-opportunities', icon: <TrendingUp className="h-5 w-5" />, capabilityKey: 'pipeline' },
      { to: '/demos', labelKey: 'demos.nav.title', testId: 'nav-demos', icon: <Monitor className="h-5 w-5" />, fallbackLabel: 'Demos' },
      { to: '/agreements', labelKey: 'agreements.nav.title', testId: 'nav-agreements', icon: <FileSignature className="h-5 w-5" />, fallbackLabel: 'Agreements' },
      { to: '/quotes', labelKey: 'quotes.nav.title', testId: 'nav-quotes', icon: <FileText className="h-5 w-5" />, fallbackLabel: 'Quotes' },
      { to: '/tasks', labelKey: 'nav.tasks', testId: 'nav-tasks', icon: <ClipboardList className="h-5 w-5" /> },
      { to: '/customers', labelKey: 'nav.customers', testId: 'nav-customers', icon: <Heart className="h-5 w-5" /> },
      { to: '/account/commission-payouts', labelKey: 'commissionPayouts.nav', testId: 'nav-my-payouts', icon: <DollarSign className="h-5 w-5" /> },
      { to: '/search', labelKey: 'nav.search', testId: 'nav-search', icon: <Search className="h-5 w-5" /> },
    ],
  },
];

const ELEVATED_NAV: NavSection[] = [
  {
    items: [
      { to: '/portfolio', labelKey: 'nav.portfolio', testId: 'nav-portfolio', icon: <Megaphone className="h-5 w-5" /> },
      { to: '/inbox', labelKey: 'nav.inbox', testId: 'nav-inbox', icon: <Inbox className="h-5 w-5" /> },
      { to: '/conversations', labelKey: 'nav.conversations', testId: 'nav-conversations', icon: <MessageSquare className="h-5 w-5" /> },
      { to: '/tasks', labelKey: 'nav.tasks', testId: 'nav-tasks', icon: <ClipboardList className="h-5 w-5" /> },
      { to: '/dashboard', labelKey: 'nav.dashboard', testId: 'nav-dashboard', icon: <LayoutDashboard className="h-5 w-5" /> },
    ],
  },
  {
    headerKey: 'nav.section.workspace',
    defaultCollapsed: true,
    items: [
      { to: '/feed', labelKey: 'livefeed.nav.title', testId: 'nav-feed', icon: <Newspaper className="h-5 w-5" />, fallbackLabel: 'Company Feed' },
      { to: '/messages', labelKey: 'chat.nav.title', testId: 'nav-messages', icon: <MessagesSquare className="h-5 w-5" />, fallbackLabel: 'Messages' },
      { to: '/workgroups', labelKey: 'workgroups.nav.title', testId: 'nav-workgroups', icon: <FolderKanban className="h-5 w-5" />, fallbackLabel: 'Workgroups' },
      { to: '/time-clock', labelKey: 'timeman.nav.title', testId: 'nav-time-clock', icon: <Clock className="h-5 w-5" />, fallbackLabel: 'Time Clock' },
      { to: '/calendar', labelKey: 'calendar.nav.title', testId: 'nav-calendar', icon: <CalendarDays className="h-5 w-5" />, fallbackLabel: 'Calendar' },
      { to: '/drive', labelKey: 'drive.nav.title', testId: 'nav-drive', icon: <HardDrive className="h-5 w-5" />, fallbackLabel: 'Drive' },
      { to: '/account/devices', labelKey: 'devices.nav.title', testId: 'nav-devices', icon: <Smartphone className="h-5 w-5" />, fallbackLabel: 'My Devices' },
      { to: '/account/email-incoming', labelKey: 'incomingEmail.nav.title', testId: 'nav-incoming-email', icon: <Mails className="h-5 w-5" />, fallbackLabel: 'Incoming Email' },
      { to: '/surveys', labelKey: 'surveys.nav.title', testId: 'nav-surveys', icon: <ClipboardList className="h-5 w-5" />, fallbackLabel: 'Surveys' },
      { to: '/esign', labelKey: 'esign.nav.title', testId: 'nav-esign', icon: <FileSignature className="h-5 w-5" />, fallbackLabel: 'E-Sign' },
    ],
  },
  {
    headerKey: 'nav.section.sales',
    defaultCollapsed: true,
    items: [
      // Records (originals)
      { to: '/campaigns', labelKey: 'nav.campaigns', testId: 'nav-campaigns', icon: <Megaphone className="h-5 w-5" /> },
      { to: '/leads', labelKey: 'nav.leads', testId: 'nav-leads', icon: <UserPlus className="h-5 w-5" /> },
      { to: '/accounts', labelKey: 'nav.accounts', testId: 'nav-accounts', icon: <Building2 className="h-5 w-5" /> },
      { to: '/opportunities', labelKey: 'nav.opportunities', testId: 'nav-opportunities', icon: <TrendingUp className="h-5 w-5" /> },
      { to: '/demos', labelKey: 'demos.nav.title', testId: 'nav-demos-elevated', icon: <Monitor className="h-5 w-5" />, fallbackLabel: 'Demos' },
      { to: '/customers', labelKey: 'nav.customers', testId: 'nav-customers', icon: <Heart className="h-5 w-5" /> },
      { to: '/search', labelKey: 'nav.search', testId: 'nav-search', icon: <Search className="h-5 w-5" /> },
      // Moved from Admin
      { to: '/admin/lead-qualification', labelKey: 'nav.leadQualification', testId: 'nav-lead-qualification', icon: <UserPlus className="h-5 w-5" /> },
      { to: '/revenue-meeting', labelKey: 'revenueCadence.meeting_title', testId: 'nav-revenue-meeting', icon: <BarChart3 className="h-5 w-5" /> },
      { to: '/admin/target-accounts', labelKey: 'nav.targetAccounts', testId: 'nav-target-accounts', icon: <Target className="h-5 w-5" /> },
      { to: '/products', labelKey: 'nav.products', testId: 'nav-products', icon: <Package className="h-5 w-5" /> },
      { to: '/admin/products/ai-agents', labelKey: 'nav.aiAgents', testId: 'nav-ai-agents', icon: <Bot className="h-5 w-5" /> },
      { to: '/admin/catalog/services', labelKey: 'nav.servicesCatalog', testId: 'nav-services-catalog', icon: <Briefcase className="h-5 w-5" /> },
      { to: '/admin/catalog/products', labelKey: 'nav.productsCatalog', testId: 'nav-products-catalog', icon: <Package className="h-5 w-5" /> },
      { to: '/quotes', labelKey: 'quotes.nav.title', testId: 'nav-quotes', icon: <FileText className="h-5 w-5" />, fallbackLabel: 'Quotes' },
      // Moved from Analytics
      { to: '/analytics/funnel', labelKey: 'nav.funnel', testId: 'nav-funnel', icon: <Funnel className="h-5 w-5" /> },
      { to: '/cockpit/rep', labelKey: 'nav.cockpitRep', testId: 'nav-cockpit-rep', icon: <Target className="h-5 w-5" /> },
      { to: '/cockpit/manager', labelKey: 'nav.cockpitManager', testId: 'nav-cockpit-manager', icon: <Compass className="h-5 w-5" /> },
      { to: '/cockpit/board', labelKey: 'nav.cockpitBoard', testId: 'nav-cockpit-board', icon: <Crown className="h-5 w-5" /> },
      { to: '/cockpit', labelKey: 'nav.cockpit', testId: 'nav-cockpit', icon: <LayoutDashboard className="h-5 w-5" /> },
      { to: '/admin/sales-team', labelKey: 'nav.salesTeam', testId: 'nav-sales-team', icon: <Users className="h-5 w-5" /> },
    ],
  },
  {
    headerKey: 'nav.section.legal',
    defaultCollapsed: true,
    items: [
      { to: '/agreements',                   labelKey: 'agreements.nav.allAgreements', testId: 'nav-agreements',             icon: <FileSignature className="h-5 w-5" />, fallbackLabel: 'All agreements' },
      { to: '/agreements?kind=MOU',          labelKey: 'agreements.nav.mou',           testId: 'nav-agreements-mou',         icon: <FileText className="h-5 w-5" />,      fallbackLabel: 'MOU' },
      { to: '/agreements?kind=NDA',          labelKey: 'agreements.nav.nda',           testId: 'nav-agreements-nda',         icon: <ShieldCheck className="h-5 w-5" />,   fallbackLabel: 'NDA' },
      { to: '/agreements?kind=PARTNERSHIP',  labelKey: 'agreements.nav.partnership',   testId: 'nav-agreements-partnership', icon: <Handshake className="h-5 w-5" />,     fallbackLabel: 'Partnership' },
      { to: '/agreements/templates',         labelKey: 'agreements.nav.templates',     testId: 'nav-agreements-templates',   icon: <Layers className="h-5 w-5" />,        fallbackLabel: 'Templates' },
    ],
  },
  {
    headerKey: 'nav.section.analytics',
    defaultCollapsed: true,
    items: [
      { to: '/analytics', labelKey: 'nav.analytics', testId: 'nav-analytics', icon: <BarChart3 className="h-5 w-5" /> },
      { to: '/analytics/channels', labelKey: 'nav.channels', testId: 'nav-channels', icon: <BarChart3 className="h-5 w-5" /> },
      { to: '/analytics/attribution', labelKey: 'nav.attribution', testId: 'nav-attribution', icon: <BarChart3 className="h-5 w-5" /> },
      { to: '/analytics/mktg-sales-align', labelKey: 'nav.mktgSalesAlign', testId: 'nav-mktg-sales-align', icon: <BarChart3 className="h-5 w-5" /> },
      { to: '/demos/analytics', labelKey: 'demos.analytics.title', testId: 'nav-demo-analytics', icon: <BarChart3 className="h-5 w-5" />, fallbackLabel: 'Demo Analytics' },
    ],
  },
  {
    headerKey: 'nav.section.admin',
    defaultCollapsed: true,
    items: [
      { to: '/admin/agents', labelKey: 'nav.agents', testId: 'nav-agents', icon: <Bot className="h-5 w-5" />, capabilityKey: 'agents' },
      { to: '/admin/text-agents', labelKey: 'nav.textAgents', testId: 'nav-text-agents', icon: <MessageSquare className="h-5 w-5" />, capabilityKey: 'agents' },
      { to: '/admin/chat-widget', labelKey: 'nav.chatWidget', testId: 'nav-chat-widget', icon: <Radio className="h-5 w-5" />, capabilityKey: 'inbox' },
      { to: '/admin/chains', labelKey: 'nav.chains', testId: 'nav-chains', icon: <Workflow className="h-5 w-5" />, capabilityKey: 'workflows' },
      { to: '/admin/workflow-runs', labelKey: 'nav.workflowRuns', testId: 'nav-workflow-runs', icon: <Workflow className="h-5 w-5" />, capabilityKey: 'workflows' },
      { to: '/admin/sequences', labelKey: 'nav.sequences', testId: 'nav-sequences', icon: <ListOrdered className="h-5 w-5" /> },
      { to: '/admin/email-pools', labelKey: 'nav.emailPools', testId: 'nav-email-pools', icon: <ServerCog className="h-5 w-5" /> },
      { to: '/admin/email-domain-setup', labelKey: 'nav.emailDomains', testId: 'nav-email-domains', icon: <Mail className="h-5 w-5" /> },
      { to: '/admin/process-map', labelKey: 'nav.processMap', testId: 'nav-process-map', icon: <Map className="h-5 w-5" /> },
      { to: '/admin/playbooks', labelKey: 'playbook.nav', testId: 'nav-playbooks', icon: <BookOpen className="h-5 w-5" /> },
      { to: '/admin/commission-tiers', labelKey: 'commissionTiers.nav', testId: 'nav-commission-tiers', icon: <DollarSign className="h-5 w-5" />, capabilityKey: 'commissions' },
      { to: '/admin/revenue-cadence', labelKey: 'revenueCadence.nav', testId: 'nav-revenue-cadence', icon: <CalendarCheck className="h-5 w-5" /> },
      { to: '/handoff-inbox', labelKey: 'handoff.inbox_title', testId: 'nav-handoff-inbox', icon: <ArrowRightLeft className="h-5 w-5" /> },
      { to: '/cockpit/am', labelKey: 'workspace.nav_am', testId: 'nav-am-cockpit', icon: <LayoutTemplate className="h-5 w-5" /> },
      { to: '/admin/lead-capture-forms', labelKey: 'nav.captureForms', testId: 'nav-capture-forms', icon: <Layers className="h-5 w-5" /> },
      { to: '/admin/landing-pages', labelKey: 'nav.landingPages', testId: 'nav-landing-pages', icon: <Layers className="h-5 w-5" /> },
      { to: '/admin/demos/brand-defaults', labelKey: 'demos.admin.brand.title', testId: 'nav-demos-brand-defaults', icon: <Monitor className="h-5 w-5" />, fallbackLabel: 'Demo brand defaults' },
      { to: '/admin/qualification-banks', labelKey: 'nav.qualificationBanks', testId: 'nav-qualification-banks', icon: <Bot className="h-5 w-5" /> },
      { to: '/qualification/conversations', labelKey: 'nav.qualificationConversations', testId: 'nav-qualification-conversations', icon: <MessageSquare className="h-5 w-5" /> },
      { to: '/admin/imports', labelKey: 'nav.imports', testId: 'nav-imports', icon: <Layers className="h-5 w-5" /> },
      { to: '/admin/dnc', labelKey: 'nav.dnc', testId: 'nav-dnc', icon: <PhoneOff className="h-5 w-5" /> },
      { to: '/admin/voice-credentials', labelKey: 'nav.voiceCreds', testId: 'nav-voice-creds', icon: <Phone className="h-5 w-5" />, capabilityKey: 'voice' },
      { to: '/admin/enrichment', labelKey: 'nav.enrichment', testId: 'nav-enrichment', icon: <Database className="h-5 w-5" /> },
      { to: '/admin/external-providers', labelKey: 'nav.externalProviders', testId: 'nav-external-providers', icon: <Plug className="h-5 w-5" /> },
      { to: '/admin/channel-lines', labelKey: 'nav.channelLines', testId: 'nav-channel-lines', icon: <Radio className="h-5 w-5" /> }, { to: '/admin/conversation-routing', labelKey: 'nav.conversationRouting', testId: 'nav-conversation-routing', icon: <Radio className="h-5 w-5" /> },
      { to: '/process-templates', labelKey: 'nav.templates', testId: 'nav-templates', icon: <Layers className="h-5 w-5" /> },
      { to: '/products', labelKey: 'nav.products', testId: 'nav-products', icon: <Package className="h-5 w-5" /> },
      { to: '/admin/billing', labelKey: 'nav.billing', testId: 'nav-billing', icon: <Coins className="h-5 w-5" />, capabilityKey: 'billing' },
      { to: '/admin/api-builder', labelKey: 'nav.apiBuilder', testId: 'nav-api-builder', icon: <Code2 className="h-5 w-5" />, capabilityKey: 'apibuilder' },
      { to: '/admin/webhooks', labelKey: 'nav.webhooks', testId: 'nav-webhooks', icon: <Zap className="h-5 w-5" /> },
      { to: '/admin/oauth-clients', labelKey: 'nav.oauthClients', testId: 'nav-oauth-clients', icon: <AppWindow className="h-5 w-5" /> },
      { to: '/admin/marketplace', labelKey: 'nav.appMarketplace', testId: 'nav-marketplace', icon: <Package className="h-5 w-5" /> },
      { to: '/admin/event-subscriptions', labelKey: 'nav.eventSubscriptions', testId: 'nav-event-subscriptions', icon: <Radio className="h-5 w-5" /> },
      { to: '/admin/inbound-webhooks', labelKey: 'nav.inboundWebhooks', testId: 'nav-inbound-webhooks', icon: <Inbox className="h-5 w-5" /> },
      { to: '/admin/segmentation', labelKey: 'nav.segmentation', testId: 'nav-segmentation', icon: <Layers className="h-5 w-5" /> },
      { to: '/admin/notifications', labelKey: 'platformNotifications.nav.title', testId: 'nav-platform-notifications', icon: <Bell className="h-5 w-5" />, fallbackLabel: 'Notifications' },
      { to: '/policies', labelKey: 'userAgreements.nav.title', testId: 'nav-user-agreements', icon: <FileText className="h-5 w-5" />, fallbackLabel: 'User Agreements' },
      { to: '/admin/extranet', labelKey: 'extranet.nav.title', testId: 'nav-extranet', icon: <Globe className="h-5 w-5" />, fallbackLabel: 'Extranet' },
      { to: '/admin/payments', labelKey: 'payments.nav.title', testId: 'nav-payments', icon: <CreditCard className="h-5 w-5" />, fallbackLabel: 'Payments', capabilityKey: 'billing' },
      { to: '/admin/warehouse', labelKey: 'warehouse.nav.title', testId: 'nav-warehouse', icon: <WarehouseIcon className="h-5 w-5" />, fallbackLabel: 'Warehouse' },
      { to: '/admin/sites', labelKey: 'sites.nav.title', testId: 'nav-sites', icon: <AppWindow className="h-5 w-5" />, fallbackLabel: 'Sites' },
      { to: '/admin/settings', labelKey: 'nav.settings', testId: 'nav-settings', icon: <Settings className="h-5 w-5" /> },
      { to: '/admin/users', labelKey: 'nav.users', testId: 'nav-users', icon: <Users className="h-5 w-5" /> },
      { to: '/admin/permissions', labelKey: 'nav.permissions', testId: 'nav-permissions', icon: <ShieldCheck className="h-5 w-5" /> },
    ],
  },
];

function navForRole(role: string | undefined): NavSection[] {
  if (role === 'TENANT_ADMIN' || role === 'SALES_MANAGER' || role === 'PLATFORM_ADMIN') {
    return ELEVATED_NAV;
  }
  return REP_NAV;
}

function loadMode(): SidebarMode {
  try {
    const v = localStorage.getItem(MODE_KEY);
    if (v === 'icons' || v === 'labels' || v === 'expanded') return v;
  } catch { /* */ }
  return 'expanded';
}

function loadCollapsedSections(): Record<string, boolean> {
  try {
    const v = localStorage.getItem(COLLAPSED_SECTIONS_KEY);
    if (v) return JSON.parse(v);
  } catch { /* */ }
  return {};
}

function saveCollapsedSections(state: Record<string, boolean>) {
  try {
    localStorage.setItem(COLLAPSED_SECTIONS_KEY, JSON.stringify(state));
  } catch { /* */ }
}

/**
 * For each section, decide if it should render collapsed. Order:
 *   1. User override in localStorage wins (true|false)
 *   2. Otherwise fall back to section.defaultCollapsed
 *   3. If the active route lives in this section, force-expand
 */
function isSectionCollapsed(
  section: NavSection,
  overrides: Record<string, boolean>,
  activePath: string,
): boolean {
  if (!section.headerKey) return false; // header-less sections always show
  const sectionContainsActive = section.items.some(
    (item) => activePath === item.to || activePath.startsWith(`${item.to}/`),
  );
  if (sectionContainsActive) return false;
  const override = overrides[section.headerKey];
  if (typeof override === 'boolean') return override;
  return section.defaultCollapsed === true;
}

export function AppShell() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const tokens = useAuthStore((s) => s.tokens);
  const branding = useBranding();

  // Federation session bridge (123.3). Publish the Tessera session on the global
  // __KAIROS_SESSION__ so federated remotes read tokens/tenant without re-running
  // OIDC. Done SYNCHRONOUSLY in render (not an effect): child effects run before
  // parent effects, so on a hard-load to /r/<cap> the RemoteHost child would
  // mount the remote BEFORE a parent effect could publish — leaving tokens null
  // at mount. Render runs top-down, so a sync publish here is set before any
  // child mounts. Cleared on logout (else branch) and on shell unmount (effect).
  if (tokens && user) {
    publishSession({
      tokens,
      tenantId: user.tenantId,
      role: user.currentRole,
      language: i18n.language,
      refresh: refreshSession,
    });
  } else {
    clearSession();
  }
  useEffect(() => () => clearSession(), []);
  // Story 121.7 — capability-driven nav. Items whose `capabilityKey`
  // is not in the effective set are hidden; sections that end up empty
  // are also hidden so users do not see lonely section headers. While
  // /me/capabilities is loading, useCapabilities.has() is optimistic
  // (returns true) so the shell does not flash an empty nav.
  const { has: hasCap } = useCapabilities();
  const filteredNav = navForRole(user?.role)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.capabilityKey || hasCap(item.capabilityKey)),
    }))
    .filter((section) => section.items.length > 0);

  // Federated plugin slots (123.5): one nav item per registered remote that
  // declares a navLabelKey and whose capability is effective. The label is used
  // as-is via t(defaultValue) so a plugin's label is deploy config, not a string
  // baked into the shell (Kairos core stays vertical-agnostic).
  const remoteNavItems: NavItem[] = Object.entries(REMOTE_REGISTRY)
    .filter(([cap, entry]) => entry.navLabelKey && hasCap(cap))
    .map(([cap, entry]) => ({
      to: `/r/${cap}`,
      labelKey: entry.navLabelKey as string,
      fallbackLabel: entry.navLabelKey,
      testId: `nav-remote-${cap}`,
      icon: <Puzzle className="h-5 w-5" />,
      capabilityKey: cap,
    }));
  if (remoteNavItems.length > 0) {
    filteredNav.push({ headerKey: 'nav.section.plugins', items: remoteNavItems });
  }

  const [mode, setModeState] = useState<SidebarMode>(loadMode);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState<Record<string, boolean>>(
    loadCollapsedSections,
  );

  function toggleSection(headerKey: string) {
    setCollapsedSections((prev) => {
      const next = { ...prev, [headerKey]: !prev[headerKey] };
      saveCollapsedSections(next);
      return next;
    });
  }

  // Browser-session idle logout. Read the effective threshold from
  // the backend on mount and on auth-state changes; default to 60 min
  // until the response lands so a quick session is still bounded.
  const [idleMinutes, setIdleMinutes] = useState<number>(60);
  useEffect(() => {
    if (!tokens) return;
    apiClient
      .get<{ idleTimeoutMinutes: number }>('/me/session-settings')
      .then(({ data }) => setIdleMinutes(data.idleTimeoutMinutes ?? 60))
      .catch(() => setIdleMinutes(60));
  }, [tokens]);
  useIdleLogout(idleMinutes);

  function setMode(m: SidebarMode) {
    setModeState(m);
    localStorage.setItem(MODE_KEY, m);
  }

  function renderNavItem(item: NavItem, sidebarMode: SidebarMode) {
    const label = t(item.labelKey);
    if (sidebarMode === 'expanded') {
      return (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={() => setMobileOpen(false)}
          data-testid={item.testId}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-all duration-200',
              isActive
                ? 'bg-sidebar-accent text-sidebar-foreground'
                : 'text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground',
            )
          }
        >
          <span className="shrink-0">{item.icon}</span>
          <span className="truncate text-[13px]">{label}</span>
        </NavLink>
      );
    }
    if (sidebarMode === 'labels') {
      return (
        <NavLink
          key={item.to}
          to={item.to}
          onClick={() => setMobileOpen(false)}
          data-testid={item.testId}
          className={({ isActive }) =>
            cn(
              'flex flex-col items-center gap-0.5 rounded-xl py-1.5 text-center transition-all duration-200',
              isActive
                ? 'bg-sidebar-accent text-sidebar-foreground'
                : 'text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground',
            )
          }
        >
          {item.icon}
          <span className="max-w-[64px] truncate text-center text-[9px] font-medium leading-tight">
            {label}
          </span>
        </NavLink>
      );
    }
    return (
      <NavLink
        key={item.to}
        to={item.to}
        onClick={() => setMobileOpen(false)}
        data-testid={item.testId}
        title={label}
        className={({ isActive }) =>
          cn(
            'flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-200',
            isActive
              ? 'bg-sidebar-accent text-sidebar-foreground'
              : 'text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground',
          )
        }
      >
        {item.icon}
      </NavLink>
    );
  }

  function sidebarContent(forMobile: boolean) {
    const m = forMobile ? 'expanded' : mode;
    const isCompact = m !== 'expanded';

    return (
      <nav className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
        {/* Header */}
        <div className={cn(
          'flex items-center px-4 py-4',
          isCompact && 'justify-center px-2',
        )}>
          {branding?.logoUrl ? (
            // Tenant branding present — render their logo. We use the
            // favicon (logomark) for the compact sidebar so it doesn't
            // get squished, and the full primary logo when expanded.
            // Dark-mode swap is handled inside the tenant's logo via
            // gradients that work on both backgrounds (Lopettia ships a
            // gradient that reads on Cream and on Deep alike).
            isCompact ? (
              <img
                src={branding.faviconUrl ?? branding.logoUrl}
                alt={t('app.name')}
                className="h-8 w-8 shrink-0"
              />
            ) : (
              <img
                src={branding.logoUrl}
                alt={t('app.name')}
                className="h-12 w-auto max-w-full"
              />
            )
          ) : (
            <>
              <img
                src="/brand/lopettia/lopettia-logomark.svg"
                alt={t('app.name')}
                className="h-8 w-8 shrink-0"
              />
              {!isCompact && (
                <div className="ml-3 min-w-0">
                  <div className="text-sm font-semibold tracking-tight">{t('app.name')}</div>
                  <div className="truncate font-mono text-[10px] uppercase tracking-[0.15em] text-sidebar-muted">
                    {t('app.tagline')}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Nav items */}
        <div className={cn(
          'flex-1 space-y-1 overflow-y-auto overflow-x-hidden py-3',
          isCompact ? 'items-center px-1' : 'px-2',
          m === 'icons' && 'flex flex-col items-center',
        )}>
          {filteredNav.map((section, sIdx) => {
            const collapsed =
              !isCompact &&
              !!section.headerKey &&
              isSectionCollapsed(section, collapsedSections, location.pathname);
            return (
              <div key={sIdx} className={sIdx > 0 && !isCompact ? 'mt-3' : ''}>
                {section.headerKey && !isCompact && (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.headerKey!)}
                    className="group mt-1 mb-1 flex w-full items-center gap-1 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-muted transition-colors hover:text-sidebar-foreground"
                    data-testid={`nav-section-${section.headerKey}`}
                    aria-expanded={!collapsed}
                  >
                    <ChevronDown
                      className={cn(
                        'h-3 w-3 transition-transform',
                        collapsed && '-rotate-90',
                      )}
                    />
                    <span>{t(section.headerKey)}</span>
                  </button>
                )}
                {!collapsed && section.items.map((item) => renderNavItem(item, m))}
              </div>
            );
          })}
        </div>

        {/* Footer — only the desktop sidebar mode slider lives here.
            Theme / language / change-password / email-settings / logout
            moved to the top-right <UserMenu /> dropdown so the sidebar
            stays focused on navigation. */}
        {!forMobile && (
          <div className="border-t border-sidebar-accent px-2 py-3">
            <ModeSlider mode={mode} onChangeMode={setMode} />
          </div>
        )}
      </nav>
    );
  }

  return (
    <div className="flex h-full min-h-screen" data-testid="app-shell">
      {/* Desktop sidebar */}
      <aside
        className={cn(
          'hidden border-r border-border transition-all duration-200 md:block',
          MODE_WIDTHS[mode],
        )}
      >
        {sidebarContent(false)}
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-64 shadow-lg">
            {sidebarContent(true)}
          </aside>
        </div>
      )}

      <main className="flex-1 overflow-y-auto">
        <MaintenanceBanner />
        <header className="sticky top-0 z-30 flex items-center gap-4 border-b border-[var(--color-lop-border)] bg-[var(--color-lop-bg-deep)]/80 px-4 py-3 backdrop-blur-md md:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="toggle menu"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <NotificationBell />
          <div className="flex-1" />
          <kbd
            className="hidden items-center gap-1 rounded-md border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] px-2 py-1 font-mono text-[11px] uppercase tracking-wider text-[var(--color-lop-text-muted)] md:inline-flex"
            title={t('palette.label')}
          >
            <span>⌘K</span>
          </kbd>
          <CreditBalanceWidget />
          <div className="hidden font-semibold tracking-tight md:block">{t('app.name')}</div>
          <UserMenu />
        </header>
        <div className="p-4 md:p-6">
          <Outlet />
        </div>
        <footer className="flex justify-end px-4 pb-2 md:px-6">
          <BuildInfo />
        </footer>
      </main>

      <CommandPalette />

      <UpdateBanner />

      <AssistantWidget />
    </div>
  );
}

function ModeSlider({
  mode,
  onChangeMode,
}: {
  mode: SidebarMode;
  onChangeMode: (m: SidebarMode) => void;
}) {
  const activeIndex = MODES.indexOf(mode);
  const [hovered, setHovered] = useState(false);
  const [pos, setPos] = useState({ bottom: 0, left: 0 });
  const anchorRef = useRef<HTMLDivElement>(null);

  function handleEnter() {
    if (anchorRef.current && mode !== 'expanded') {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({ bottom: window.innerHeight - rect.bottom, left: rect.left });
    }
    setHovered(true);
  }

  const slider = (
    <div className="relative flex items-center rounded-full bg-sidebar-accent p-0.5">
      <div
        className="absolute h-[calc(100%-4px)] w-[26px] rounded-full bg-primary/20 shadow-sm transition-all duration-200 ease-out"
        style={{ left: `${2 + activeIndex * 30}px` }}
      />
      {MODES.map((m) => (
        <button
          key={m}
          onClick={() => {
            onChangeMode(m);
            setHovered(false);
          }}
          className={cn(
            'relative z-10 flex h-7 w-[28px] items-center justify-center rounded-full transition-colors duration-150',
            mode === m
              ? 'text-primary'
              : 'text-sidebar-muted hover:text-sidebar-foreground',
          )}
        >
          {MODE_ICONS[m]}
        </button>
      ))}
    </div>
  );

  if (mode === 'expanded') {
    return <div className="flex justify-center pt-1">{slider}</div>;
  }

  return (
    <div className="flex justify-center pt-1">
      <div
        ref={anchorRef}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setHovered(false)}
        className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-sidebar-accent"
      >
        <span className="text-primary">{MODE_ICONS[mode]}</span>
      </div>

      {hovered && (
        <div
          style={{ position: 'fixed', bottom: pos.bottom, left: pos.left, zIndex: 60 }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <div className="rounded-full shadow-lg">{slider}</div>
        </div>
      )}
    </div>
  );
}

// ---- Notification types + helpers ----

interface NotificationItem {
  id: string;
  userId: string;
  type: string;
  payloadJson: string;
  readAt: string | null;
  createdAt: string | null;
}

const NOTIFICATION_TYPE_CONFIG: Record<string, { icon: React.ReactNode; color: string }> = {
  STALE_OPPORTUNITY: { icon: <AlertTriangle className="h-4 w-4" />, color: 'text-amber-500' },
  AGENT_OUTPUT: { icon: <Cpu className="h-4 w-4" />, color: 'text-purple-500' },
  TASK_ASSIGNED: { icon: <CheckSquare className="h-4 w-4" />, color: 'text-blue-500' },
  MENTION: { icon: <AtSign className="h-4 w-4" />, color: 'text-emerald-500' },
  APPROVAL_REQUESTED: { icon: <ShieldAlert className="h-4 w-4" />, color: 'text-red-500' },
  BILLING_LOW_BALANCE: { icon: <Coins className="h-4 w-4" />, color: 'text-amber-500' },
};

type Translate = (key: string, opts?: Record<string, unknown>) => string;

function relativeTime(dateStr: string | null, t: Translate): string {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('notifications.time.justNow');
  if (mins < 60) return t('notifications.time.minutesAgo', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 24) return t('notifications.time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('notifications.time.daysAgo', { count: days });
}

/**
 * Turn a notification's structured payload into readable, localized copy.
 * Backend payloads are intentionally structured (e.g. BILLING_LOW_BALANCE
 * carries { balance, threshold, auto_topup_enabled }) rather than a baked-in
 * English sentence, so the human-readable string is composed here, per type.
 * Never render the raw JSON — unknown types fall back to a generic line.
 */
function formatNotification(n: NotificationItem, t: Translate): { title: string; detail: string } {
  let payload: Record<string, unknown> = {};
  try {
    payload = JSON.parse(n.payloadJson) as Record<string, unknown>;
  } catch {
    // Malformed payload — fall through to the generic copy below.
  }

  switch (n.type) {
    case 'BILLING_LOW_BALANCE': {
      const balance = Number(payload.balance ?? 0);
      const threshold = Number(payload.threshold ?? 0);
      const autoTopup = Boolean(payload.auto_topup_enabled);
      const body = t('notifications.types.billingLowBalance.body', { balance, threshold });
      const topup = t(
        autoTopup
          ? 'notifications.types.billingLowBalance.autoTopupOn'
          : 'notifications.types.billingLowBalance.autoTopupOff',
      );
      return { title: t('notifications.types.billingLowBalance.title'), detail: `${body} ${topup}` };
    }
    default: {
      const msg = payload.message ?? payload.summary ?? payload.title;
      const title =
        typeof msg === 'string' && msg.trim().length > 0 ? msg : t('notifications.genericBody');
      return { title, detail: '' };
    }
  }
}

function NotificationBell() {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [available, setAvailable] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const [listRes, countRes] = await Promise.all([
        apiClient.get<NotificationItem[]>('/notifications'),
        apiClient.get<{ count: number }>('/notifications/unread-count'),
      ]);
      setNotifications(listRes.data);
      setUnreadCount(countRes.data.count);
      setAvailable(true);
    } catch {
      setAvailable(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  async function markRead(id: string) {
    try {
      await apiClient.post(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, readAt: new Date().toISOString() } : n,
        ),
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch {
      // ignore
    }
    setOpen(false);
  }

  async function markAllRead() {
    const unread = notifications.filter((n) => !n.readAt);
    try {
      await Promise.all(unread.map((n) => apiClient.post(`/notifications/${n.id}/read`)));
      setNotifications((prev) =>
        prev.map((n) => (n.readAt ? n : { ...n, readAt: new Date().toISOString() })),
      );
      setUnreadCount(0);
    } catch {
      // ignore
    }
  }

  if (!available) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
        aria-label={t('notifications.ariaLabel')}
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-80 rounded-lg border bg-card shadow-lg">
          <div className="border-b px-4 py-3">
            <h3 className="text-sm font-semibold">{t('notifications.title')}</h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                {t('notifications.empty')}
              </div>
            ) : (
              notifications.map((n) => {
                const config = NOTIFICATION_TYPE_CONFIG[n.type] ?? {
                  icon: <Bell className="h-4 w-4" />,
                  color: 'text-muted-foreground',
                };
                const { title, detail } = formatNotification(n, t);
                return (
                  <button
                    key={n.id}
                    onClick={() => markRead(n.id)}
                    className={cn(
                      'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/60',
                      !n.readAt && 'bg-primary/5',
                    )}
                  >
                    <span className={cn('mt-0.5 shrink-0', config.color)}>
                      {config.icon}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className={cn('break-words text-sm text-foreground', !n.readAt && 'font-medium')}>
                        {title}
                      </p>
                      {detail && (
                        <p className="mt-0.5 break-words text-xs text-muted-foreground">
                          {detail}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-muted-foreground">
                        {relativeTime(n.createdAt, t)}
                      </p>
                    </div>
                    {!n.readAt && (
                      <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                    )}
                  </button>
                );
              })
            )}
          </div>
          {unreadCount > 0 && (
            <div className="border-t px-4 py-2">
              <button
                onClick={markAllRead}
                className="text-xs font-medium text-primary hover:underline"
              >
                {t('notifications.markAllRead')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
